import axios from "axios";

/**
 * HTTP client for the anonymous recipient opt-out endpoints (kitecorp/transferify-api-service#222).
 *
 * Recipients of a transfer have no account — their address was supplied by a sender — so the signed
 * token from the email link is the only credential these calls carry. Axios already has `baseURL` and
 * the auth interceptor configured globally, and the endpoints are `permitAll`, so an absent session is
 * expected rather than an error.
 */

const OPT_OUT_PATH = "/email/opt-out";

/** What a token refers to, as reported by the api. */
export interface OptOutStatus {
	email: string;
	unsubscribed: boolean;
}

/**
 * Resolves what a token names WITHOUT unsubscribing anyone — the api deliberately keeps the read
 * separate from the write so a mail scanner following the link cannot opt somebody out.
 *
 * @throws when the token is missing, malformed, or not authentic (api replies 400)
 */
export async function fetchOptOutStatus(token: string): Promise<OptOutStatus> {
	const {data} = await axios.get<OptOutStatus>(OPT_OUT_PATH, {params: {token}});
	return data;
}

/**
 * Records the opt-out. Idempotent server-side, so re-submitting an already-used link succeeds instead
 * of reporting an error the recipient can do nothing about.
 */
export async function submitOptOut(token: string): Promise<OptOutStatus> {
	const {data} = await axios.post<OptOutStatus>(OPT_OUT_PATH, null, {params: {token}});
	return data;
}
